import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import { User } from '../../types';
import styles from './Comptes.module.css';

interface ClientPhysique {
  id: number; nom: string; role: string; createdAt: string;
  nbAchats: number; dernierAchat: string | null;
}

interface ClientDetail {
  client: { id: number; nom: string; createdAt: string };
  ventes: {
    id: number; type: string; dateVente: string | null;
    modePaiement: string; nbLignes: number; totalTtc: number; beneficeHt: number; createdAt: string;
  }[];
}

const fmt = (v: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

export const Comptes: React.FC = () => {
  const [tab, setTab] = useState<'online' | 'physique'>('online');

  // En ligne
  const [users, setUsers]       = useState<User[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [editMode, setEditMode] = useState(false);

  // Physiques
  const [clients, setClients]               = useState<ClientPhysique[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientDetail, setClientDetail]     = useState<ClientDetail | null>(null);
  const [searchClient, setSearchClient]     = useState('');

  useEffect(() => { loadUsers(); }, []);

  useEffect(() => {
    if (tab === 'physique' && clients.length === 0) loadClients();
  }, [tab]);

  const loadUsers = async () => {
    try {
      setLoading(true); setError('');
      const r = await api.get<User[]>('users');
      setUsers(r.data);
    } catch (err: any) {
      setError('Erreur : ' + (err.message || 'Erreur inconnue'));
    } finally { setLoading(false); }
  };

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const r = await api.get<ClientPhysique[]>('ventes/clients');
      setClients(r.data ?? []);
    } catch { } finally { setLoadingClients(false); }
  };

  const openClientDetail = async (clientId: number) => {
    try {
      const r = await api.get<ClientDetail>(`ventes/clients/${clientId}`);
      setClientDetail(r.data);
    } catch { alert('Erreur chargement historique.'); }
  };

  const handleAdd = () => {
    setUsers([...users, {
      id: null, firstName: 'Nouveau', lastName: 'Utilisateur',
      email: 'exemple@email.com', passwordHash: '',
      birthDate: new Date().toISOString(), createdAt: new Date().toISOString(),
      company: '', role: 'User',
    }]);
    setEditMode(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      for (const user of users) {
        const payload = {
          firstName: user.firstName, lastName: user.lastName, email: user.email,
          passwordHash: user.passwordHash, birthDate: user.birthDate,
          company: user.company, role: user.role,
        };
        if (!user.id) await api.post('users', payload);
        else await api.put(`users/${user.id}`, payload);
      }
      alert('Modifications enregistrées !');
      loadUsers();
    } catch (err: any) {
      alert('Erreur : ' + (err.response?.data?.message || err.message));
    } finally { setLoading(false); }
  };

  const handleDelete = async (userId: number | null | undefined) => {
    if (!userId) return;
    if (!window.confirm(`Supprimer le compte ID ${userId} ?`)) return;
    try { await api.delete(`users/${userId}`); loadUsers(); }
    catch (err: any) { alert('Erreur : ' + (err.response?.data?.message || err.message)); }
  };

  const handleFieldChange = (index: number, field: keyof User, value: string) => {
    const updated = [...users];
    updated[index] = { ...updated[index], [field]: value };
    setUsers(updated);
  };

  const physiques = clients.filter(c => c.role === 'physique');
  const filteredClients = physiques.filter(c =>
    !searchClient.trim() || c.nom.toLowerCase().includes(searchClient.toLowerCase())
  );

  return (
    <Layout>
      <div className={styles.container} style={{ padding: '2rem' }}>
        <h1 className={styles.title}>Comptes</h1>

        {/* Onglets */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${tab === 'online' ? styles.tabActive : ''}`}
            onClick={() => { setTab('online'); setClientDetail(null); }}
          >
            🌐 En ligne ({users.length})
          </button>
          <button
            className={`${styles.tab} ${tab === 'physique' ? styles.tabActive : ''}`}
            onClick={() => setTab('physique')}
          >
            🏪 Clients physiques {physiques.length > 0 ? `(${physiques.length})` : ''}
          </button>
        </div>

        {/* ── En ligne ── */}
        {tab === 'online' && (
          <>
            <div className={styles.actions}>
              <button onClick={handleAdd} className={styles.btnAdd} disabled={loading}>Ajouter</button>
              <button onClick={() => { alert('Modifie directement les cellules puis clique sur "Enregistrer".'); setEditMode(true); }} className={styles.btnEdit} disabled={loading}>Modifier</button>
              <button onClick={handleSave} className={styles.btnSave} disabled={loading || !editMode}>Enregistrer</button>
            </div>

            {error && <div className={styles.error}>{error}</div>}
            {loading && <div className={styles.loading}>Chargement...</div>}

            {!loading && users.length > 0 && (
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Id</th><th>Prénom</th><th>Nom</th><th>Email</th>
                      <th>Naissance</th><th>Entreprise</th><th>Rôle</th><th>Créé le</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, i) => (
                      <tr key={user.id || `new-${i}`}>
                        <td>{user.id || '-'}</td>
                        <td>{editMode ? <input className={styles.input} value={user.firstName || ''} onChange={e => handleFieldChange(i, 'firstName', e.target.value)} /> : user.firstName}</td>
                        <td>{editMode ? <input className={styles.input} value={user.lastName || ''} onChange={e => handleFieldChange(i, 'lastName', e.target.value)} /> : user.lastName}</td>
                        <td>{editMode ? <input className={styles.input} type="email" value={user.email || ''} onChange={e => handleFieldChange(i, 'email', e.target.value)} /> : user.email}</td>
                        <td>{editMode ? <input className={styles.input} type="date" value={user.birthDate?.split('T')[0] ?? ''} onChange={e => handleFieldChange(i, 'birthDate', e.target.value)} /> : (user.birthDate ? fmtDate(user.birthDate) : '-')}</td>
                        <td>{editMode ? <input className={styles.input} value={user.company || ''} onChange={e => handleFieldChange(i, 'company', e.target.value)} /> : (user.company || '-')}</td>
                        <td>{editMode ? <select className={styles.input} value={user.role || 'User'} onChange={e => handleFieldChange(i, 'role', e.target.value)}><option value="User">User</option><option value="Admin">Admin</option></select> : user.role}</td>
                        <td>{user.createdAt ? fmtDate(user.createdAt) : '-'}</td>
                        <td><button className={styles.btnDelete} onClick={() => handleDelete(user.id)} disabled={loading}>Supprimer</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ── Clients physiques ── */}
        {tab === 'physique' && (
          <>
            {clientDetail ? (
              /* Détail client */
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
                  <button className={styles.btnEdit} onClick={() => setClientDetail(null)} style={{ padding: '.4rem .8rem' }}>← Retour</button>
                  <h2 style={{ margin: 0, fontSize: '1.2rem' }}>👤 {clientDetail.client.nom}</h2>
                  <span style={{ color: '#a0aec0', fontSize: '.85rem' }}>Client depuis le {fmtDate(clientDetail.client.createdAt)}</span>
                </div>

                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Achats', val: String(clientDetail.ventes.length) },
                    { label: 'CA total TTC', val: fmt(clientDetail.ventes.reduce((s, v) => s + v.totalTtc, 0)) },
                    { label: 'Bénéfice HT', val: fmt(clientDetail.ventes.reduce((s, v) => s + v.beneficeHt, 0)) },
                    { label: 'Panier moyen', val: clientDetail.ventes.length > 0 ? fmt(clientDetail.ventes.reduce((s, v) => s + v.totalTtc, 0) / clientDetail.ventes.length) : '—' },
                  ].map(s => (
                    <div key={s.label} style={{ background: '#fff', borderRadius: 10, padding: '1rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,.07)', minWidth: 140 }}>
                      <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#2d3748' }}>{s.val}</div>
                      <div style={{ fontSize: '.75rem', color: '#718096', textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr><th>#</th><th>Type</th><th>Date</th><th>Produits</th><th>Total TTC</th><th>Bénéfice HT</th><th>Paiement</th></tr>
                    </thead>
                    <tbody>
                      {clientDetail.ventes.map(v => (
                        <tr key={v.id}>
                          <td>#{v.id}</td>
                          <td>{v.type === 'physique' ? '🏪' : v.type === 'online' ? '🌐' : '🎪'} {v.type}</td>
                          <td style={{ color: '#718096' }}>{fmtDate(v.dateVente)}</td>
                          <td style={{ textAlign: 'center' }}>{v.nbLignes}</td>
                          <td style={{ fontWeight: 700 }}>{fmt(v.totalTtc)}</td>
                          <td style={{ color: v.beneficeHt >= 0 ? '#276749' : '#c53030', fontWeight: 600 }}>{fmt(v.beneficeHt)}</td>
                          <td style={{ color: '#718096' }}>{v.modePaiement}</td>
                        </tr>
                      ))}
                      {clientDetail.ventes.length === 0 && (
                        <tr><td colSpan={7} className={styles.noData}>Aucun achat enregistré.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* Liste clients */
              <>
                <input
                  type="text"
                  placeholder="🔍 Rechercher un client..."
                  value={searchClient}
                  onChange={e => setSearchClient(e.target.value)}
                  style={{ padding: '.5rem .75rem', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '.875rem', width: 280, outline: 'none', marginBottom: '1rem' }}
                />

                {loadingClients && <div className={styles.loading}>Chargement...</div>}

                {!loadingClients && (
                  <div className={styles.tableContainer}>
                    <table className={styles.table}>
                      <thead>
                        <tr><th>#</th><th>Nom</th><th>Client depuis</th><th>Nb achats</th><th>Dernier achat</th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        {filteredClients.map(c => (
                          <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => openClientDetail(c.id)}>
                            <td>#{c.id}</td>
                            <td style={{ fontWeight: 600 }}>👤 {c.nom}</td>
                            <td style={{ color: '#718096' }}>{fmtDate(c.createdAt)}</td>
                            <td style={{ textAlign: 'center' }}>{c.nbAchats}</td>
                            <td style={{ color: '#718096' }}>{fmtDate(c.dernierAchat)}</td>
                            <td>
                              <button className={styles.btnEdit} onClick={e => { e.stopPropagation(); openClientDetail(c.id); }}>
                                📋 Historique
                              </button>
                            </td>
                          </tr>
                        ))}
                        {filteredClients.length === 0 && (
                          <tr><td colSpan={6} className={styles.noData}>
                            {clients.length === 0
                              ? 'Aucun client physique — ils apparaîtront après les premières ventes.'
                              : 'Aucun client trouvé.'}
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};
